import { useEffect } from 'react';

let activeLockCount = 0;
let lockedScrollY = 0;
let previousStyles = null;
let removeTouchOverscrollGuard = null;

function isScrollableOverflow(overflowValue) {
  return overflowValue === 'auto' || overflowValue === 'scroll' || overflowValue === 'overlay';
}

function findScrollableAncestor(startNode) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return null;
  }

  let node = startNode instanceof Element ? startNode : null;
  while (node && node !== document.body) {
    const style = window.getComputedStyle(node);
    const canScrollY =
      isScrollableOverflow(style.overflowY) &&
      node.scrollHeight > node.clientHeight + 1;
    const canScrollX =
      isScrollableOverflow(style.overflowX) &&
      node.scrollWidth > node.clientWidth + 1;

    if (canScrollY || canScrollX) {
      return node;
    }

    node = node.parentElement;
  }

  return null;
}

function attachTouchOverscrollGuard() {
  if (typeof document === 'undefined') {
    return () => {};
  }

  let touchStartY = 0;
  let touchStartX = 0;
  let startScrollable = null;

  const handleTouchStart = (event) => {
    if (!event.touches || event.touches.length !== 1) {
      startScrollable = null;
      return;
    }

    const touch = event.touches[0];
    touchStartY = touch.clientY;
    touchStartX = touch.clientX;
    startScrollable = findScrollableAncestor(event.target);
  };

  const handleTouchMove = (event) => {
    if (!event.cancelable || !event.touches || event.touches.length !== 1) {
      return;
    }

    const touch = event.touches[0];
    const deltaY = touch.clientY - touchStartY;
    const deltaX = touch.clientX - touchStartX;
    const scrollable = startScrollable || findScrollableAncestor(event.target);

    // 스크롤 가능한 컨테이너가 없으면 배경 당김(바운스)을 차단한다.
    if (!scrollable) {
      event.preventDefault();
      return;
    }

    const canScrollY = scrollable.scrollHeight > scrollable.clientHeight + 1;
    const canScrollX = scrollable.scrollWidth > scrollable.clientWidth + 1;

    if (canScrollY) {
      const atTop = scrollable.scrollTop <= 0;
      const atBottom =
        scrollable.scrollTop + scrollable.clientHeight >= scrollable.scrollHeight - 1;

      if ((atTop && deltaY > 0) || (atBottom && deltaY < 0)) {
        event.preventDefault();
        return;
      }
    }

    if (canScrollX) {
      const atLeft = scrollable.scrollLeft <= 0;
      const atRight =
        scrollable.scrollLeft + scrollable.clientWidth >= scrollable.scrollWidth - 1;

      if ((atLeft && deltaX > 0) || (atRight && deltaX < 0)) {
        event.preventDefault();
      }
    }
  };

  document.addEventListener('touchstart', handleTouchStart, { passive: true });
  document.addEventListener('touchmove', handleTouchMove, { passive: false });

  return () => {
    document.removeEventListener('touchstart', handleTouchStart);
    document.removeEventListener('touchmove', handleTouchMove);
  };
}

function lockBodyScroll() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const { body, documentElement } = document;
  if (!body || !documentElement) {
    return;
  }

  if (activeLockCount === 0) {
    lockedScrollY =
      window.scrollY || window.pageYOffset || documentElement.scrollTop || 0;
    previousStyles = {
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
      bodyOverscrollBehavior: body.style.overscrollBehavior,
      htmlOverscrollBehavior: documentElement.style.overscrollBehavior,
    };

    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${lockedScrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overscrollBehavior = 'none';
    documentElement.style.overscrollBehavior = 'none';
    removeTouchOverscrollGuard = attachTouchOverscrollGuard();
  }

  activeLockCount += 1;
}

function unlockBodyScroll() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const { body, documentElement } = document;
  if (!body || !documentElement || activeLockCount <= 0) {
    return;
  }

  activeLockCount -= 1;
  if (activeLockCount > 0) {
    return;
  }

  const styles = previousStyles || {};
  body.style.overflow = styles.bodyOverflow || '';
  body.style.position = styles.bodyPosition || '';
  body.style.top = styles.bodyTop || '';
  body.style.left = styles.bodyLeft || '';
  body.style.right = styles.bodyRight || '';
  body.style.width = styles.bodyWidth || '';
  body.style.overscrollBehavior = styles.bodyOverscrollBehavior || '';
  documentElement.style.overscrollBehavior = styles.htmlOverscrollBehavior || '';
  if (removeTouchOverscrollGuard) {
    removeTouchOverscrollGuard();
    removeTouchOverscrollGuard = null;
  }

  window.scrollTo(0, lockedScrollY);
  previousStyles = null;
}

function useBodyScrollLock(locked) {
  useEffect(() => {
    if (!locked) {
      return undefined;
    }

    lockBodyScroll();
    return () => {
      unlockBodyScroll();
    };
  }, [locked]);
}

export default useBodyScrollLock;
